import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Play, Plus, Trash2, X, Video as VideoIcon, ArrowLeft } from 'lucide-react';

const Videos = ({ user, logout }) => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ youtube_url: '', title: '', description: '', category: '' });

  const isAdmin = user && user.is_admin;

  const loadData = useCallback(async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        api.get('/videos'),
        api.get('/videos/categories'),
      ]);
      setVideos(vRes.data);
      setCategories(cRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to view videos.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load videos.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredVideos = activeCategory === 'All'
    ? videos
    : videos.filter((v) => v.category === activeCategory);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.youtube_url.trim() || !form.title.trim()) {
      toast.error('YouTube URL and title are required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/videos', {
        youtube_url: form.youtube_url.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category.trim() || 'General',
      });
      toast.success('Video added!');
      setForm({ youtube_url: '', title: '', description: '', category: '' });
      setShowAddForm(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add video.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await api.delete(`/videos/${videoId}`);
      toast.success('Video deleted.');
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete video.');
    }
  };

  const tabs = ['All', ...categories];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/dashboard" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="videos-back-btn">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline font-medium">Dashboard</span>
            </Link>
            <div className="flex items-center space-x-2 pl-2 border-l border-gray-200">
              <div className="w-9 h-9 bg-red-600 rounded-lg flex items-center justify-center">
                <VideoIcon className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Videos</h1>
            </div>
          </div>
          {isAdmin && (
            <Button
              data-testid="add-video-btn"
              onClick={() => setShowAddForm((s) => !s)}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full px-5"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Video
            </Button>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Intro */}
        <div className="mb-8 max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Diagnosis Video Library
          </h2>
          <p className="text-gray-600">Watch curated TCM diagnosis videos. Click any card to play.</p>
        </div>

        {/* Admin Add Form */}
        {isAdmin && showAddForm && (
          <form
            onSubmit={handleAdd}
            data-testid="add-video-form"
            className="bg-white border border-gray-200 rounded-2xl p-6 mb-8 shadow-sm space-y-4 max-w-2xl"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add a New Video</h3>
              <button type="button" onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL <span className="text-red-500">*</span></label>
              <Input
                data-testid="video-url-input"
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
              <Input
                data-testid="video-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Reading the Tongue: Pale & Swollen"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category / Topic</label>
              <Input
                data-testid="video-category-input"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="e.g., Tongue, Pulse, Diagnosis (default: General)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea
                data-testid="video-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description of the video"
                rows={3}
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="submit-video-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
              {submitting ? 'Adding...' : 'Add Video'}
            </Button>
          </form>
        )}

        {/* Category Filter */}
        {tabs.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="video-category-filter">
            {tabs.map((cat) => (
              <button
                key={cat}
                data-testid={`category-tab-${cat}`}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-red-300 hover:text-red-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Video Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="text-center py-20" data-testid="videos-empty-state">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <VideoIcon className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-gray-500 text-lg">No videos yet.</p>
            {isAdmin && <p className="text-gray-400 text-sm mt-1">Click "Add Video" to publish your first one.</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
            {filteredVideos.map((video) => (
              <div
                key={video.id}
                data-testid={`video-card-${video.id}`}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <button
                  onClick={() => setActiveVideo(video)}
                  className="relative block w-full aspect-video bg-gray-900 overflow-hidden"
                  data-testid={`play-video-${video.id}`}
                >
                  <img
                    src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                    <span className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
                    </span>
                  </span>
                </button>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <span className="inline-block text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-0.5 rounded-full mb-2">
                      {video.category}
                    </span>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(video.id)}
                        data-testid={`delete-video-${video.id}`}
                        className="text-gray-300 hover:text-red-600 transition-colors"
                        title="Delete video"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 leading-snug line-clamp-2">{video.title}</h3>
                  {video.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{video.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Video Player Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
          data-testid="video-player-modal"
        >
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-lg pr-4 line-clamp-1">{activeVideo.title}</h3>
              <button
                onClick={() => setActiveVideo(null)}
                data-testid="close-player-btn"
                className="text-white/80 hover:text-white flex-shrink-0"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
              <iframe
                title={activeVideo.title}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${activeVideo.youtube_id}?autoplay=1&rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            {activeVideo.description && (
              <p className="text-gray-300 text-sm mt-3">{activeVideo.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Videos;
